import { Link } from 'react-router-dom';

/**
 * Getting Started Guide for Super Admins
 * Platform administration and multi-tenant management
 */
export default function SuperAdminGuide() {
  return (
    <div className="page guide-page">
      <div className="page-header">
        <h1>Super Admin Guide</h1>
        <p className="page-subtitle">
          Platform administration guide for managing MediaRite at the system level.
        </p>
      </div>

      <div className="guide-content">
        <section className="guide-section">
          <h2>1. Super Admin Role</h2>
          <p>
            As a Super Admin, you have system-wide access to manage all tenants (studios),
            users, and platform configuration. You can also operate as a regular studio admin
            for your own studio.
          </p>

          <h3>Access Points</h3>
          <ul>
            <li>
              <strong>Super Admin Console</strong> -
              <a href="/superadminconsole" target="_blank" rel="noopener noreferrer">
                /superadminconsole
              </a>
              <br />
              <span className="text-muted">Protected by Cloudflare Access</span>
            </li>
            <li>
              <strong>Regular Dashboard</strong> - <Link to="/dashboard">/dashboard</Link>
              <br />
              <span className="text-muted">Your studio view + admin features</span>
            </li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>2. Tenant Management</h2>
          <p>
            The <Link to="/admin/tenants">Tenant Management</Link> page shows all studios
            on the platform.
          </p>

          <h3>Viewing Tenants</h3>
          <ul>
            <li><strong>Name</strong> - Studio/business name</li>
            <li><strong>Status</strong> - Active, Suspended, or Pending</li>
            <li><strong>Created</strong> - When the tenant was created</li>
            <li><strong>Users</strong> - Number of users in the tenant</li>
            <li><strong>Documents</strong> - Total documents created</li>
          </ul>

          <h3>Tenant Actions</h3>
          <ul>
            <li><strong>View Details</strong> - See full tenant information</li>
            <li><strong>Suspend</strong> - Temporarily disable tenant access</li>
            <li><strong>Activate</strong> - Re-enable a suspended tenant</li>
            <li><strong>Impersonate</strong> - Log in as tenant admin (for support)</li>
          </ul>

          <div className="guide-warning">
            <strong>Caution:</strong> Impersonation logs are recorded for audit purposes.
            Only use this feature for legitimate support needs.
          </div>
        </section>

        <section className="guide-section">
          <h2>3. Backup Management</h2>
          <p>
            The <Link to="/admin/backups">Backup Management</Link> page handles system and
            tenant backups.
          </p>

          <h3>System Backups</h3>
          <ul>
            <li><strong>Automatic</strong> - Daily system backups run automatically</li>
            <li><strong>Manual</strong> - Trigger a full system backup on demand</li>
            <li><strong>Download</strong> - Download backup files for offline storage</li>
          </ul>

          <h3>Tenant Backups</h3>
          <ul>
            <li><strong>Export Tenant</strong> - Create a backup of a specific tenant</li>
            <li><strong>Restore Tenant</strong> - Restore from a backup file</li>
            <li><strong>Migration</strong> - Move tenant data between environments</li>
          </ul>

          <div className="guide-note">
            <strong>Best Practice:</strong> Download and store system backups in multiple
            locations. Cloudflare R2 provides redundancy, but off-platform backups are
            recommended for disaster recovery.
          </div>
        </section>

        <section className="guide-section">
          <h2>4. Super Admin Console</h2>
          <p>
            The <a href="/superadminconsole">Super Admin Console</a> provides direct API
            access and debugging tools.
          </p>

          <h3>Console Features</h3>
          <ul>
            <li><strong>API Explorer</strong> - Test API endpoints directly</li>
            <li><strong>Database Queries</strong> - Run read-only queries</li>
            <li><strong>Log Viewer</strong> - View system logs</li>
            <li><strong>Health Checks</strong> - System status overview</li>
          </ul>

          <div className="guide-warning">
            <strong>Security:</strong> The Super Admin Console is protected by Cloudflare
            Access and requires additional authentication. Ensure your Cloudflare Access
            policies are properly configured.
          </div>
        </section>

        <section className="guide-section">
          <h2>5. Your Studio Access</h2>
          <p>
            As a Super Admin, you also have a regular studio account. You can use all the
            normal studio features from your <Link to="/dashboard">Dashboard</Link>.
          </p>

          <h3>Studio Features</h3>
          <ul>
            <li><Link to="/models">Manage Models</Link> - Your studio's talent roster</li>
            <li><Link to="/productions">Productions</Link> - Your studio's productions</li>
            <li><Link to="/documents">Documents</Link> - Your studio's documents</li>
            <li><Link to="/compliance">Compliance</Link> - Your studio's compliance status</li>
            <li><Link to="/users">Users</Link> - Your studio's staff members</li>
            <li><Link to="/settings">Settings</Link> - Your studio configuration</li>
          </ul>

          <p>
            For detailed instructions on these features, see the{' '}
            <Link to="/guide/studio">Studio Admin Guide</Link>.
          </p>
        </section>

        <section className="guide-section">
          <h2>6. Platform Monitoring</h2>

          <h3>Key Metrics to Watch</h3>
          <ul>
            <li><strong>Active Tenants</strong> - Number of studios using the platform</li>
            <li><strong>Document Volume</strong> - Documents created per day/week</li>
            <li><strong>Storage Usage</strong> - R2 bucket usage for files</li>
            <li><strong>API Response Times</strong> - Performance metrics</li>
            <li><strong>Error Rates</strong> - Failed requests and errors</li>
          </ul>

          <h3>External Monitoring</h3>
          <ul>
            <li><strong>Cloudflare Dashboard</strong> - Workers, Pages, D1, R2 metrics</li>
            <li><strong>Cloudflare Analytics</strong> - Traffic and performance data</li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>7. Common Administrative Tasks</h2>

          <h3>Onboarding a New Studio</h3>
          <ol>
            <li>Studio owner visits <code>/studioregister</code></li>
            <li>They create their account and studio</li>
            <li>You may receive notification of new tenant</li>
            <li>Review tenant details in <Link to="/admin/tenants">Tenant Management</Link></li>
            <li>Activate if manual approval is required</li>
          </ol>

          <h3>Supporting a Studio</h3>
          <ol>
            <li>Go to <Link to="/admin/tenants">Tenant Management</Link></li>
            <li>Find the tenant and view their details</li>
            <li>Use impersonation if needed to see their view</li>
            <li>Make any necessary changes</li>
            <li>Document the support action</li>
          </ol>

          <h3>Handling a Data Request</h3>
          <ol>
            <li>Identify the tenant making the request</li>
            <li>Create a tenant backup</li>
            <li>Download and provide the backup file</li>
            <li>Log the data request for compliance</li>
          </ol>
        </section>

        <section className="guide-section">
          <h2>API Reference</h2>
          <p>Key API endpoints for Super Admin operations:</p>

          <ul>
            <li><code>GET /api/admin/tenants</code> - List all tenants</li>
            <li><code>GET /api/admin/tenants/:id</code> - Get tenant details</li>
            <li><code>POST /api/admin/tenants/:id/suspend</code> - Suspend tenant</li>
            <li><code>POST /api/admin/tenants/:id/activate</code> - Activate tenant</li>
            <li><code>POST /api/admin/backup/system</code> - Create system backup</li>
            <li><code>POST /api/admin/tenants/:id/backup</code> - Backup specific tenant</li>
            <li><code>GET /api/health</code> - System health check</li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>Need Help?</h2>
          <p>
            For platform issues, check the Cloudflare dashboard for errors and metrics.
            For application issues, check the logs in the Super Admin Console.
          </p>
        </section>

        <div className="guide-actions">
          <Link to="/dashboard" className="btn btn-primary">
            Go to Dashboard
          </Link>
          <Link to="/admin/tenants" className="btn btn-secondary">
            Manage Tenants
          </Link>
          <a href="/superadminconsole" className="btn btn-secondary">
            Super Admin Console
          </a>
        </div>
      </div>
    </div>
  );
}
